/**
 * PaymentSuccess - Payment success callback page
 * Polls Stripe checkout status and shows result
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useTranslation } from "../translations";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = ({ onNavigate }) => {
  const [status, setStatus] = useState("polling");
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) {
      pollStatus(sessionId, 0);
    } else {
      setStatus("error");
      setError("No session ID found");
    }
  }, []);

  const pollStatus = async (sessionId, attempt) => {
    const maxAttempts = 8;
    const interval = 2000;

    if (attempt >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/stripe/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(res.data);
        return;
      } else if (res.data.status === "expired") {
        setStatus("expired");
        return;
      }

      setTimeout(() => pollStatus(sessionId, attempt + 1), interval);
    } catch (err) {
      if (attempt < maxAttempts - 1) {
        setTimeout(() => pollStatus(sessionId, attempt + 1), interval);
      } else {
        setStatus("error");
        setError(err.message);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md" data-testid="payment-result-card">
        <CardContent className="pt-8 pb-8 text-center">
          {status === "polling" && (
            <div data-testid="payment-polling">
              <Loader2 className="w-16 h-16 mx-auto text-blue-500 animate-spin mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">{t('processingPayment') || 'Processando pagamento...'}</h2>
              <p className="text-gray-600">{t('pleaseWait') || 'Aguarde enquanto confirmamos seu pagamento.'}</p>
            </div>
          )}

          {status === "success" && (
            <div data-testid="payment-success">
              <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h2 className="text-xl font-bold text-green-700 mb-2">{t('paymentConfirmedTitle') || 'Pagamento confirmado!'}</h2>
              <p className="text-gray-600 mb-6">{t('paymentConfirmedDesc') || 'Seu plano foi ativado com sucesso.'}</p>
              {paymentData?.plan_id && (
                <p className="text-sm text-gray-500 mb-4">
                  {t('plan')}: <strong>{paymentData.plan_id}</strong>
                </p>
              )}
              <Button
                data-testid="go-to-dashboard-btn"
                className="bg-green-600 hover:bg-green-700 w-full"
                onClick={() => {
                  window.history.pushState({}, '', '/');
                  if (onNavigate) onNavigate('dashboard');
                  else window.location.href = '/';
                }}
              >
                {t('goToDashboard') || 'Ir para o Dashboard'}
              </Button>
            </div>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <div data-testid="payment-error">
              <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-bold text-red-700 mb-2">
                {status === "timeout" 
                  ? (t('paymentTimeout') || 'Tempo esgotado')
                  : (t('paymentError') || 'Erro no pagamento')}
              </h2>
              <p className="text-gray-600 mb-6">
                {status === "timeout"
                  ? (t('paymentTimeoutDesc') || 'Não foi possível confirmar. Verifique seu email.')
                  : (error || t('paymentErrorDesc') || 'Ocorreu um erro. Tente novamente.')}
              </p>
              <Button
                data-testid="back-to-plans-btn"
                variant="outline"
                className="w-full"
                onClick={() => {
                  window.history.pushState({}, '', '/');
                  if (onNavigate) onNavigate('plans');
                  else window.location.href = '/';
                }}
              >
                {t('backToPlans') || 'Voltar aos Planos'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
